import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Form, 
  Button, 
  Alert, 
  Spinner, 
  Badge,
  Modal,
  Pagination
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { policyAPI, classroomAPI, childAPI } from '../utils/api';
import type { Policy, CreatePolicyRequest } from '../types/policy';
import type { Classroom } from '../types/classroom';
import type { Child } from '../types/child';
import type { User } from '../types/auth';
import { formatDateTimeInNurseryTimezone, getTodayDateInNurseryTimezone } from '../utils/timezone';

// Icons
const PolicyIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 1 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
  </svg>
);

const AddIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16" className="text-primary" style={{ cursor: 'pointer' }}>
    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
  </svg>
);

const ViewIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16" className="text-info" style={{ cursor: 'pointer' }}>
    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16" className="text-danger" style={{ cursor: 'pointer' }}>
    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
  </svg>
);

// Custom MultiSelect Dropdown Component
interface MultiSelectOption {
  value: string | number;
  label: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  selectedValues: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options...",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      // Small delay to prevent rapid touch events
      setTimeout(() => {
        setIsOpen(!isOpen);
      }, 10);
    }
  };

  const handleOptionToggle = (value: string | number, e?: React.MouseEvent | React.TouchEvent | React.ChangeEvent<HTMLInputElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('🔄 Toggling option:', value, 'Current values:', selectedValues);
    if (selectedValues.includes(value)) {
      const newValues = selectedValues.filter(v => v !== value);
      console.log('✅ Removing value, new values:', newValues);
      onChange(newValues);
    } else {
      const newValues = [...selectedValues, value];
      console.log('✅ Adding value, new values:', newValues);
      onChange(newValues);
    }
  };

  const handleSelectAll = (e?: React.MouseEvent | React.TouchEvent | React.ChangeEvent<HTMLInputElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const allValues = options.map(option => option.value);
    onChange(allValues);
  };

  const handleDeselectAll = (e?: React.MouseEvent | React.TouchEvent | React.ChangeEvent<HTMLInputElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onChange([]);
  };

  const handleClickOutside = (event: MouseEvent | TouchEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    // Add both mouse and touch events for better mobile support
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const displayText = selectedValues.length > 0 
    ? `${selectedValues.length} selected` 
    : placeholder;

  const allSelected = selectedValues.length === options.length && options.length > 0;
  const someSelected = selectedValues.length > 0 && selectedValues.length < options.length;

  return (
    <div ref={dropdownRef} className="position-relative">
      <div className="position-relative">
        <Form.Control
          type="text"
          value={displayText}
          onClick={handleToggle}
          onTouchStart={handleToggle}
          readOnly
          disabled={disabled}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
        <div 
          className="position-absolute"
          style={{ 
            right: '12px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: disabled ? '#6c757d' : '#495057'
          }}
        >
          <svg 
            width="12" 
            height="12" 
            fill="currentColor" 
            viewBox="0 0 16 16"
            style={{ 
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          >
            <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
          </svg>
        </div>
      </div>
      {isOpen && (
        <div 
          className="position-absolute w-100 bg-white border rounded shadow-lg"
          style={{ 
            top: '100%', 
            left: 0, 
            zIndex: 9999,
            maxHeight: '200px',
            overflowY: 'auto'
          }}
        >
          {/* Select All / Deselect All Options */}
          {options.length > 0 && (
            <div className="border-bottom">
              <div
                className="px-3 py-2 d-flex align-items-center"
                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                onClick={(e) => {
                  // Handle click on the entire row (except checkbox)
                  if (e.target === e.currentTarget || e.target === e.currentTarget.querySelector('span')) {
                    if (allSelected) {
                      handleDeselectAll(e);
                    } else {
                      handleSelectAll(e);
                    }
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (allSelected) {
                      handleDeselectAll(e);
                    } else {
                      handleSelectAll(e);
                    }
                  }}
                  className="form-check-input me-2"
                  style={{ 
                    cursor: 'pointer',
                    transform: 'scale(1.1)',
                    accentColor: '#0d6efd'
                  }}
                  key={`select-all-${allSelected}-${someSelected}`}
                />
                <span className="fw-semibold text-primary">
                  {allSelected ? 'Deselect All' : 'Select All'}
                </span>
              </div>
            </div>
          )}
          
          {/* Individual Options */}
          {options.map((option) => (
            <div
              key={option.value}
              className="px-3 py-2 d-flex align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                // Handle click on the entire row (except checkbox)
                if (e.target === e.currentTarget || e.target === e.currentTarget.querySelector('span')) {
                  handleOptionToggle(option.value, e);
                }
              }}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleOptionToggle(option.value, e);
                }}
                className="form-check-input me-2"
                style={{ 
                  cursor: 'pointer',
                  transform: 'scale(1.1)',
                  accentColor: '#0d6efd'
                }}
                key={`${option.value}-${selectedValues.includes(option.value)}`}
              />
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const ACTION_TYPE_KEYS: Record<string, string> = {
  enter_classroom: 'form.detected',
  not_detected: 'form.notDetected',
  detected: 'form.detectedGeneric',
};


const Policies: React.FC = () => {
  const { currentNursery, userRolePermissionsLoading } = useAuth();
  const { canAdd, canEdit, canDelete, canView } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'policies' });
  const { t: tc } = useTranslation('common');
  
  const actionTypeLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const [key, translationKey] of Object.entries(ACTION_TYPE_KEYS)) {
      labels[key] = t(translationKey);
    }
    return labels;
  }, [t]);

  // Data state
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [viewingPolicy, setViewingPolicy] = useState<Policy | null>(null);
  const [deletingPolicy, setDeletingPolicy] = useState<Policy | null>(null);
  const [viewModalLoading, setViewModalLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // Form state for create
  const [formData, setFormData] = useState<CreatePolicyRequest>({
    nursery_id: currentNursery?.nursery_id || '',
    name: '',
    is_enabled: true,
    alert_level: 'grey',
    notify_channels: ['in_app'],
    action_type: 'enter_classroom',
    detection_method: 'classroom',
    time_mode: 'anytime',
    days_of_week: [],
    start_time: '09:00',
    end_time: '17:00',
    date_start: '',
    date_end: '',
    timeslap: 15,
    repeat_interval_minutes: 180,
    classrooms: [],
    locations: [],
    child_ids: [],
    user_ids: []
  });

  // Load data
  const loadPolicies = useCallback(async () => {
    if (!currentNursery?.nursery_id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await policyAPI.getPolicies(currentNursery.nursery_id, {});
      
      // Handle legacy array response and wrapped API responses.
      if (Array.isArray(response)) {
        setPolicies(response);
      } else if (Array.isArray(response.data)) {
        setPolicies(response.data);
      } else if (Array.isArray(response.policies)) {
        setPolicies(response.policies);
      } else {
        setPolicies([]);
      }
    } catch (err: any) {
      setError(err.message || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery?.nursery_id, t]);

  const loadDropdownData = useCallback(async () => {
    if (!currentNursery?.nursery_id) return;
    
    try {
      const [classroomsResponse, childrenResponse] = await Promise.all([
        classroomAPI.getClassrooms(currentNursery.nursery_id),
        childAPI.getChildren(currentNursery.nursery_id)
      ]);
      
      setClassrooms(classroomsResponse.classrooms || []);
      
      if (childrenResponse.success) {
        setChildren(childrenResponse.children || []);
      }
      
      // Fetch users using the new endpoint
      try {
        const token = localStorage.getItem('jwttoken');
        const response = await fetch(
          `${import.meta.env.DEV ? '/api/' : import.meta.env.VITE_API_URL || 'https://api.littlelens.ai/api/'}usersByNursery/${currentNursery.nursery_id}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const usersData = await response.json();
          if (usersData.success) {
            setUsers(usersData.data || []);
          }
        } else {
          console.error('Failed to fetch users:', response.status);
        }
      } catch (userError) {
        console.error('Error fetching users:', userError);
      }
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  }, [currentNursery?.nursery_id]);

  // Filtered and paginated data
  const filteredPolicies = useMemo(() => {
    let filtered = policies;
    
    if (searchTerm) {
      filtered = filtered.filter(policy => 
        policy.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  }, [policies, searchTerm]);

  const paginatedPolicies = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredPolicies.slice(startIndex, endIndex);
  }, [filteredPolicies, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredPolicies.length / rowsPerPage);

  // Event handlers
  const handleCreatePolicy = async () => {
    try {
      setLoading(true);
      
      // Prepare the request data based on action_type and detection_method
      const requestData: CreatePolicyRequest = {
        ...formData,
        // Remove duplicate user IDs
        user_ids: [...new Set(formData.user_ids)],
        // Locations are always required
        locations: [...new Set(formData.locations)].map(Number),
        // Convert days_of_week array to appropriate format if needed
        days_of_week: formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' 
          ? formData.days_of_week 
          : undefined,
        // Only include time_mode for enter_classroom action
        time_mode: formData.action_type === 'enter_classroom' ? formData.time_mode : undefined,
        // Only include timeslap for not_detected actions
        timeslap: formData.action_type === 'not_detected' ? formData.timeslap : undefined,
        // Only include time fields if within_window
        start_time: formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' 
          ? formData.start_time 
          : undefined,
        end_time: formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' 
          ? formData.end_time 
          : undefined,
        date_start: formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' 
          ? formData.date_start 
          : undefined,
        date_end: formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' 
          ? formData.date_end 
          : undefined,
        // Notification cooldown interval
        repeat_interval_minutes: formData.repeat_interval_minutes || 180,
        // Classroom or child based on detection_method
        classrooms: formData.detection_method === 'classroom' 
          ? [...new Set(formData.classrooms || [])].map(Number)
          : undefined,
        child_ids: formData.detection_method === 'child' 
          ? [...new Set(formData.child_ids || [])].map(Number)
          : undefined,
      };

      // Debug: Log the request data
      console.log('📤 Creating policy with data:', requestData);

      const response = await policyAPI.createPolicy(requestData);
      
      if (response.success) {
        setShowCreateModal(false);
        resetForm();
        loadPolicies();
      }
    } catch (err: any) {
      setCreateError(err.response?.data?.message || err.message || t('createFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle edit policy
  const handleEditPolicy = (policy: Policy) => {
    setEditingPolicy(policy);
    
    // Helper function to safely parse days_of_week
    const parseDaysOfWeek = (days: any): string[] => {
      if (!days) return [];
      if (Array.isArray(days)) return days;
      if (typeof days === 'string') return days.split(',').map(d => d.trim()).filter(d => d);
      return [];
    };
    
    // Pre-fill form with policy data
    setFormData({
      nursery_id: policy.nursery_id,
      name: policy.name,
      is_enabled: policy.is_enabled,
      alert_level: policy.alert_level,
      notify_channels: Array.isArray(policy.notify_channels) ? policy.notify_channels : [],
      action_type: policy.action_type,
      detection_method: policy.detection_method,
      time_mode: policy.time_mode || 'anytime',
      days_of_week: parseDaysOfWeek(policy.days_of_week),
      start_time: formatTimeForInput(policy.start_time),
      end_time: formatTimeForInput(policy.end_time),
      date_start: formatDateForInput(policy.date_start),
      date_end: formatDateForInput(policy.date_end),
      timeslap: policy.timeslap || 15,
      repeat_interval_minutes: policy.repeat_interval_minutes || 180,
      classrooms: policy.classrooms?.map(d => d.classroom_id) || [],
      locations: policy.locations?.map(l => l.classroom_id) || [],
      child_ids: policy.targets?.map(t => t.child_id) || [],
      user_ids: policy.recipients?.map(r => r.user_id) || []
    });
    
    setEditError(null);
    setShowEditModal(true);
  };

  // Handle edit policy submission
  const handleEditSubmit = async () => {
    if (!editingPolicy) return;
    
    try {
      setLoading(true);
      
      // Prepare the request data (same as create, but without nursery_id)
      const requestData: Omit<CreatePolicyRequest, 'nursery_id'> = {
        name: formData.name,
        is_enabled: formData.is_enabled,
        alert_level: formData.alert_level,
        notify_channels: [...new Set(formData.notify_channels)],
        action_type: formData.action_type,
        detection_method: formData.detection_method,
        // Remove duplicate user IDs
        user_ids: [...new Set(formData.user_ids)],
        // Locations are always required
        locations: [...new Set(formData.locations)].map(Number),
        // Convert days_of_week array to appropriate format if needed
        days_of_week: formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' 
          ? formData.days_of_week 
          : undefined,
        // Only include time_mode for enter_classroom action
        time_mode: formData.action_type === 'enter_classroom' ? formData.time_mode : undefined,
        // Only include timeslap for not_detected actions
        timeslap: formData.action_type === 'not_detected' ? formData.timeslap : undefined,
        // Only include time fields if within_window
        start_time: formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' 
          ? formData.start_time 
          : undefined,
        end_time: formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' 
          ? formData.end_time 
          : undefined,
        date_start: formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' 
          ? formData.date_start 
          : undefined,
        date_end: formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' 
          ? formData.date_end 
          : undefined,
        // Notification cooldown interval
        repeat_interval_minutes: formData.repeat_interval_minutes || 180,
        // Classroom or child based on detection_method
        classrooms: formData.detection_method === 'classroom' 
          ? [...new Set(formData.classrooms || [])].map(Number)
          : undefined,
        child_ids: formData.detection_method === 'child' 
          ? [...new Set(formData.child_ids || [])].map(Number)
          : undefined
      };

      // Debug: Log the request data
      console.log('📤 Updating policy with data:', requestData);

      const response = await policyAPI.updatePolicy(editingPolicy.id, requestData);
      
      if (response.success) {
        setShowEditModal(false);
        setEditingPolicy(null);
        resetForm();
        loadPolicies();
      }
    } catch (err: any) {
      setEditError(err.response?.data?.message || err.message || t('updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert backend time format to HTML input format (HH:MM)
  const formatTimeForInput = (time: string | null | undefined): string => {
    if (!time) return '09:00';
    
    // If already in HH:MM format, return as is
    if (/^\d{2}:\d{2}$/.test(time)) {
      return time;
    }
    
    // Handle HH:MM:SS format (e.g., "09:00:00" to "09:00")
    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time.substring(0, 5); // Take first 5 characters (HH:MM)
    }
    
    // Convert H:i to HH:MM (e.g., "9:00" to "09:00")
    const parts = time.split(':');
    if (parts.length === 2) {
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    
    return '09:00';
  };

  // Helper function to format date for input (convert ISO datetime to YYYY-MM-DD)
  const formatDateForInput = (date: string | null | undefined): string => {
    if (!date) return '';
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    
    // Try to parse and format the date
    try {
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().split('T')[0]; // YYYY-MM-DD
      }
    } catch (error) {
      console.log('⚠️ Error parsing date:', date, error);
    }
    
    return '';
  };

  // Handle view policy
  const handleViewPolicy = async (policy: Policy) => {
    try {
      setViewError(null);
      setViewModalLoading(true);
      setViewingPolicy(null); // Clear previous data
      
      const response = await policyAPI.getPolicy(policy.id);
      
      if (response.success) {
        setViewingPolicy(response.policy);
        setShowViewModal(true);
      }
    } catch (err: any) {
      setViewError(err.response?.data?.message || err.message || t('loadDetailsFailed'));
      setShowViewModal(true);
    } finally {
      setViewModalLoading(false);
    }
  };

  // Handle delete policy
  const handleDeletePolicy = async () => {
    if (!deletingPolicy) return;
    
    try {
      setLoading(true);
      
      const response = await policyAPI.deletePolicy(deletingPolicy.id);
      
      if (response.success) {
        setShowDeleteModal(false);
        setDeletingPolicy(null);
        loadPolicies();
      }
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || err.message || t('deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Close view modal
  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingPolicy(null);
    setViewModalLoading(false);
    setViewError(null);
  };

  const resetForm = () => {
    setFormData({
      nursery_id: currentNursery?.nursery_id || '',
      name: '',
      is_enabled: true,
      alert_level: 'grey',
      notify_channels: ['in_app'],
      action_type: 'enter_classroom',
      detection_method: 'classroom',
      time_mode: 'anytime',
      days_of_week: [],
      start_time: '09:00',
      end_time: '17:00',
      date_start: '',
      date_end: '',
      timeslap: 15,
      repeat_interval_minutes: 180,
      classrooms: [],
      locations: [],
      child_ids: [],
      user_ids: []
    });
  };

  // Effects
  useEffect(() => {
    setSearchTerm('');
    setCurrentPage(1);
    if (currentNursery?.nursery_id) {
      setFormData(prev => ({ ...prev, nursery_id: currentNursery.nursery_id }));
    }
  }, [currentNursery]);

  useEffect(() => {
    if (currentNursery) {
      loadPolicies();
      loadDropdownData();
    }
  }, [currentNursery, loadPolicies, loadDropdownData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Show loading state while role permissions are being loaded
  if (userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>{t('loadingPermissions')}</span>
          </div>
        </Container>
      </Layout>
    );
  }

  // Check if user has permission to view policies
  if (!canView('policy')) {
    return (
      <Layout title={t('title')}>
        <Container fluid>
          <Alert variant="danger">
            {t('noAccess')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  if (!currentNursery) {
    return (
      <Layout title={t('title')}>
        <Container fluid>
          <Alert variant="warning">
            {t('selectNursery')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3">
        {/* Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
          <h2 className="fw-bold text-primary mb-3 mb-md-0">
            {t('title')}
          </h2>
          {currentNursery && canAdd('policy') && (
            <Button 
              variant="primary" 
              onClick={() => { setCreateError(null); setShowCreateModal(true); }}
            >
              <AddIcon />
              {t('createPolicy')}
            </Button>
          )}
        </div>

        <Alert variant="info" className="mb-4">
          <strong>{t('managingFor')}</strong> {currentNursery.description} (ID: {currentNursery.nursery_id})
        </Alert>

        {error && (
          <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t('filtersTitle')}</h5>
              <small className="text-muted">
                {tc('labels.nursery')}: {currentNursery.description}
              </small>
            </div>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={(e) => { e.preventDefault(); }}>
              <Row>
                <Col lg={3} md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('searchPlaceholder')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Button type="button" variant="outline-secondary" onClick={() => setSearchTerm('')}>
                      {tc('buttons.clearFilters')}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* Policies Table */}
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                {t('policyRecords', { count: filteredPolicies.length })}
              </h5>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" className="me-3" />
                <span>{t('loading')}</span>
              </div>
            ) : paginatedPolicies.length === 0 ? (
              <div className="text-center py-4 py-md-5">
                <div style={{ fontSize: '3rem', opacity: '0.3', marginBottom: '1rem' }}>📋</div>
                <h5 className="mb-2 fw-bold text-muted">{t('noPoliciesTitle')}</h5>
                <p className="text-muted mb-0 small">
                  {searchTerm ? t('noMatch') : t('noneCreated')}
                </p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="d-none d-md-block">
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('columns.name')}</th>
                        <th>{t('columns.status')}</th>
                        <th>{t('columns.alertLevel')}</th>
                        <th>{t('columns.actionType')}</th>
                        <th>{t('columns.detectionMethod')}</th>
                        <th>{t('columns.locations')}</th>
                        <th>{t('columns.recipients')}</th>
                        <th>{t('columns.targets')}</th>
                        <th>{tc('labels.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPolicies.map((policy) => (
                          <tr key={policy.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <PolicyIcon />
                                <span className="ms-2 fw-semibold">{policy.name}</span>
                              </div>
                            </td>
                            <td>
                              <Badge bg={policy.is_enabled ? 'success' : 'secondary'}>
                                {policy.is_enabled ? t('enabled') : t('disabled')}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={policy.alert_level === 'red' ? 'danger' : 
                                       policy.alert_level === 'yellow' ? 'warning' : 'secondary'}>
                                {policy.alert_level.toUpperCase()}
                              </Badge>
                            </td>
                            <td>
                              <small className="text-muted">
                                {actionTypeLabels[policy.action_type] || policy.action_type}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {policy.detection_method === 'classroom' ? t('byClassroom') : t('byChild')}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {t('locationCount', { count: policy.locations?.length || 0 })}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {t('userCount', { count: policy.recipients?.length || 0 })}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {policy.detection_method === 'classroom' 
                                  ? t('classroomCount', { count: policy.classrooms?.length || 0 })
                                  : t('childCount', { count: policy.targets?.length || 0 })
                                }
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                {canView('policy') && (
                                  <span
                                    onClick={() => handleViewPolicy(policy)}
                                    style={{ cursor: 'pointer' }}
                                    title={t('viewPolicy')}
                                  >
                                    <ViewIcon />
                                  </span>
                                )}
                                {canEdit('policy') && (
                                  <span
                                    onClick={() => handleEditPolicy(policy)}
                                    style={{ cursor: 'pointer' }}
                                    title={t('editPolicy')}
                                  >
                                    <EditIcon />
                                  </span>
                                )}
                                {canDelete('policy') && (
                                  <span
                                    onClick={() => {
                                      setDeletingPolicy(policy);
                                      setDeleteError(null);
                                      setShowDeleteModal(true);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                    title={t('deletePolicy')}
                                  >
                                    <DeleteIcon />
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="d-md-none">
                  {paginatedPolicies.map((policy) => (
                    <Card key={policy.id} className="mb-3 border-0 shadow-sm">
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold">{policy.name}</h6>
                            <small className="text-muted">{actionTypeLabels[policy.action_type] || policy.action_type}</small>
                          </div>
                          <Badge bg={policy.is_enabled ? 'success' : 'secondary'} className="px-2 py-1">
                            {policy.is_enabled ? t('enabled') : t('disabled')}
                          </Badge>
                        </div>
                        <Row className="g-2">
                          <Col xs={6}>
                            <small className="text-muted d-block">{t('columns.alertLevel')}</small>
                            <Badge bg={policy.alert_level === 'red' ? 'danger' : 
                                     policy.alert_level === 'yellow' ? 'warning' : 'secondary'} className="px-2 py-1">
                              {policy.alert_level.toUpperCase()}
                            </Badge>
                          </Col>
                          <Col xs={6}>
                            <small className="text-muted d-block">{t('columns.detectionMethod')}</small>
                            <small className="fw-medium">
                              {policy.detection_method === 'classroom' ? t('byClassroom') : t('byChild')}
                            </small>
                          </Col>
                          <Col xs={6}>
                            <small className="text-muted d-block">{t('columns.locations')}</small>
                            <span className="fw-medium text-primary">
                              {t('locationCount', { count: policy.locations?.length || 0 })}
                            </span>
                          </Col>
                          <Col xs={6}>
                            <small className="text-muted d-block">{t('columns.recipients')}</small>
                            <small className="text-muted">
                              {t('userCount', { count: policy.recipients?.length || 0 })}
                            </small>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2 mt-3 pt-2 border-top">
                          {canView('policy') && (
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleViewPolicy(policy)}
                              className="d-flex align-items-center"
                            >
                              <ViewIcon />
                              <span className="ms-1">{tc('buttons.view')}</span>
                            </Button>
                          )}
                          {canEdit('policy') && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditPolicy(policy)}
                              className="d-flex align-items-center"
                            >
                              <EditIcon />
                              <span className="ms-1">{tc('buttons.edit')}</span>
                            </Button>
                          )}
                          {canDelete('policy') && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setDeletingPolicy(policy);
                                setDeleteError(null);
                                setShowDeleteModal(true);
                              }}
                              className="d-flex align-items-center"
                            >
                              <DeleteIcon />
                              <span className="ms-1">{tc('buttons.delete')}</span>
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>

                {/* Pagination */}
                {filteredPolicies.length > 0 && (
                  <div className="d-flex justify-content-between align-items-center p-3 border-top">
                    <div className="d-flex align-items-center gap-3">
                      <span className="text-muted small">
                        {tc('pagination.showing', { from: ((currentPage - 1) * rowsPerPage) + 1, to: Math.min(currentPage * rowsPerPage, filteredPolicies.length), total: filteredPolicies.length })}
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">{tc('pagination.itemsPerPage')}</span>
                        <Form.Select
                          size="sm"
                          style={{ width: 'auto' }}
                          value={rowsPerPage}
                          onChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </Form.Select>
                      </div>
                    </div>
                    <Pagination className="mb-0">
                      <Pagination.Prev 
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      />
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next 
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        {/* Create Policy Modal */}
        <Modal show={showCreateModal} onHide={() => { setCreateError(null); setShowCreateModal(false); }} size="lg" centered scrollable>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('createNewPolicy')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={(e) => { e.preventDefault(); handleCreatePolicy(); }}>
            <Modal.Body className="py-2 py-md-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {createError && (
                <Alert variant="danger" dismissible onClose={() => setCreateError(null)} className="mb-3">
                  {createError}
                </Alert>
              )}
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.nurseryId')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.nursery_id}
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.policyName')} *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('form.enterPolicyName')}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.alertLevel')} *</Form.Label>
                    <Form.Select
                      value={formData.alert_level}
                      onChange={(e) => setFormData(prev => ({ ...prev, alert_level: e.target.value as any }))}
                      required
                    >
                      <option value="grey">{t('form.grey')}</option>
                      <option value="yellow">{t('form.yellow')}</option>
                      <option value="red">{t('form.red')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('columns.status')}</Form.Label>
                    <Form.Check
                      type="switch"
                      label={t('enabled')}
                      checked={formData.is_enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_enabled: e.target.checked }))}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.actionType')} *</Form.Label>
                    <Form.Select
                      value={formData.action_type}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        action_type: e.target.value as any
                      }))}
                    >
                      <option value="enter_classroom">{t('form.detected')}</option>
                      <option value="not_detected">{t('form.notDetected')}</option>
                      <option value="detected">{t('form.detectedGeneric')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.targetedLocations')} *</Form.Label>
                    <MultiSelectDropdown
                      options={classrooms.map(classroom => ({ value: classroom.id, label: classroom.name }))}
                      selectedValues={formData.locations}
                      onChange={(values) => setFormData(prev => ({ ...prev, locations: values as number[] }))}
                      placeholder={t('form.selectLocations')}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.detectionMethod')} *</Form.Label>
                    <Form.Select
                      value={formData.detection_method}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        detection_method: e.target.value as any,
                        classrooms: [],
                        child_ids: []
                      }))}
                    >
                      <option value="classroom">{t('byClassroom')}</option>
                      <option value="child">{t('byChild')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {/* Time Mode for Detected */}
                {formData.action_type === 'enter_classroom' && (
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t('form.timeMode')} *</Form.Label>
                      <Form.Select
                        value={formData.time_mode}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          time_mode: e.target.value as any
                        }))}
                      >
                        <option value="anytime">{t('form.anytime')}</option>
                        <option value="within_window">{t('form.withinWindow')}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              {/* Timeslap for Not Detected */}
              {(formData.action_type === 'not_detected') && (
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t('form.timeInterval')} *</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={formData.timeslap}
                        onChange={(e) => setFormData(prev => ({ ...prev, timeslap: Number(e.target.value) }))}
                        placeholder={t('form.timeIntervalPlaceholder')}
                      />
                      <Form.Text className="text-muted">
                        {t('form.timeIntervalHelp')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {/* Time Window Fields */}
              {formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' && (
                <>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.startTime')} *</Form.Label>
                        <Form.Control
                          type="time"
                          value={formData.start_time}
                          onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.endTime')} *</Form.Label>
                        <Form.Control
                          type="time"
                          value={formData.end_time}
                          onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.dateStart')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.date_start}
                          min={getTodayDateInNurseryTimezone(currentNursery?.timezone || 'UTC')}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_start: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.dateEnd')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.date_end}
                          min={formData.date_start || getTodayDateInNurseryTimezone(currentNursery?.timezone || 'UTC')}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_end: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <Form.Label>{t('form.daysOfWeek')}</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                        <Form.Check
                          key={day}
                          type="checkbox"
                          label={t(`form.days.${day.toLowerCase()}`, { defaultValue: day })}
                          checked={formData.days_of_week?.includes(day)}
                          onChange={(e) => {
                            const currentDays = formData.days_of_week || [];
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                days_of_week: [...currentDays, day]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                days_of_week: currentDays.filter(d => d !== day)
                              }));
                            }
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Target Selection */}
              {formData.detection_method === 'classroom' ? (
              <div className="mb-3">
                <Form.Label>{t('form.targetedByClassrooms')} *</Form.Label>
                  <MultiSelectDropdown
                    options={classrooms.map(classroom => ({ value: classroom.id, label: classroom.name }))}
                    selectedValues={formData.classrooms || []}
                    onChange={(values) => setFormData(prev => ({ ...prev, classrooms: values as number[] }))}
                  placeholder={t('form.selectClassrooms')}
                />
              </div>
              ) : (
              <div className="mb-3">
                <Form.Label>{t('form.targetChildren')} *</Form.Label>
                  <MultiSelectDropdown
                    options={children.map(child => ({ value: child.id, label: `${child.name} (${child.classroom})` }))}
                    selectedValues={formData.child_ids || []}
                    onChange={(values) => setFormData(prev => ({ ...prev, child_ids: values as number[] }))}
                  placeholder={t('form.selectChildren')}
                />
              </div>
              )}

              <div className="mb-3">
                <Form.Label>{t('form.notificationRecipients')} *</Form.Label>
                <MultiSelectDropdown
                  options={users.map(user => ({ value: user.id, label: `${user.name} (${user.email})` }))}
                  selectedValues={formData.user_ids}
                  onChange={(values) => setFormData(prev => ({ ...prev, user_ids: values as number[] }))}
                  placeholder={t('form.selectUsers')}
                />
              </div>

              <div className="mb-3">
                <Form.Label>{t('form.notificationChannels')} *</Form.Label>
                <MultiSelectDropdown
                  options={['in_app', 'email', 'sms', 'webhook'].map(channel => ({ 
                    value: channel, 
                    label: channel.replace('_', ' ').toUpperCase() 
                  }))}
                  selectedValues={formData.notify_channels}
                  onChange={(values) => setFormData(prev => ({ ...prev, notify_channels: values as string[] }))}
                  placeholder={t('form.selectChannels')}
                />
              </div>

              <div className="mb-3">
                <Form.Label>{t('form.notificationCooldown')}</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={formData.repeat_interval_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, repeat_interval_minutes: Number(e.target.value) }))}
                  placeholder="180"
                />
                <Form.Text className="text-muted">
                  {t('form.cooldownHelp')}
                </Form.Text>
              </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={loading}>
              {tc('buttons.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {tc('status.creating')}
                </>
              ) : (
                <>
                  <AddIcon />
                  {t('createPolicy')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

        {/* Edit Policy Modal */}
        <Modal show={showEditModal} onHide={() => {
          setShowEditModal(false);
          setEditingPolicy(null);
          setEditError(null);
          resetForm();
        }} size="lg" centered scrollable>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('editPolicy')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={(e) => { e.preventDefault(); handleEditSubmit(); }}>
            <Modal.Body className="py-2 py-md-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {editError && (
                <Alert variant="danger" dismissible onClose={() => setEditError(null)} className="mb-3">
                  {editError}
                </Alert>
              )}
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.nurseryId')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.nursery_id}
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.policyName')} *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('form.enterPolicyName')}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.alertLevel')} *</Form.Label>
                    <Form.Select
                      value={formData.alert_level}
                      onChange={(e) => setFormData(prev => ({ ...prev, alert_level: e.target.value as any }))}
                      required
                    >
                      <option value="grey">{t('form.grey')}</option>
                      <option value="yellow">{t('form.yellow')}</option>
                      <option value="red">{t('form.red')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('columns.status')}</Form.Label>
                    <Form.Check
                      type="switch"
                      label={t('enabled')}
                      checked={formData.is_enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_enabled: e.target.checked }))}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.actionType')} *</Form.Label>
                    <Form.Select
                      value={formData.action_type}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        action_type: e.target.value as any
                      }))}
                    >
                      <option value="enter_classroom">{t('form.detected')}</option>
                      <option value="not_detected">{t('form.notDetected')}</option>
                      <option value="detected">{t('form.detectedGeneric')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.targetedLocations')} *</Form.Label>
                    <MultiSelectDropdown
                      options={classrooms.map(classroom => ({ value: classroom.id, label: classroom.name }))}
                      selectedValues={formData.locations}
                      onChange={(values) => setFormData(prev => ({ ...prev, locations: values as number[] }))}
                      placeholder={t('form.selectLocations')}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.detectionMethod')} *</Form.Label>
                    <Form.Select
                      value={formData.detection_method}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        detection_method: e.target.value as any,
                        classrooms: [],
                        child_ids: []
                      }))}
                    >
                      <option value="classroom">{t('byClassroom')}</option>
                      <option value="child">{t('byChild')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {/* Time Mode for Detected */}
                {formData.action_type === 'enter_classroom' && (
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t('form.timeMode')} *</Form.Label>
                      <Form.Select
                        value={formData.time_mode}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          time_mode: e.target.value as any
                        }))}
                      >
                        <option value="anytime">{t('form.anytime')}</option>
                        <option value="within_window">{t('form.withinWindow')}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              {/* Timeslap for Not Detected */}
              {(formData.action_type === 'not_detected') && (
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t('form.timeInterval')} *</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={formData.timeslap}
                        onChange={(e) => setFormData(prev => ({ ...prev, timeslap: Number(e.target.value) }))}
                        placeholder={t('form.timeIntervalPlaceholder')}
                      />
                      <Form.Text className="text-muted">
                        {t('form.timeIntervalHelp')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {/* Time Window Fields */}
              {formData.action_type === 'enter_classroom' && formData.time_mode === 'within_window' && (
                <>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.startTime')} *</Form.Label>
                        <Form.Control
                          type="time"
                          value={formData.start_time}
                          onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.endTime')} *</Form.Label>
                        <Form.Control
                          type="time"
                          value={formData.end_time}
                          onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.dateStart')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.date_start}
                          min={getTodayDateInNurseryTimezone(currentNursery?.timezone || 'UTC')}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_start: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.dateEnd')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.date_end}
                          min={formData.date_start || getTodayDateInNurseryTimezone(currentNursery?.timezone || 'UTC')}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_end: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <Form.Label>{t('form.daysOfWeek')}</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                        <Form.Check
                          key={day}
                          type="checkbox"
                          label={t(`form.days.${day.toLowerCase()}`, { defaultValue: day })}
                          checked={formData.days_of_week?.includes(day)}
                          onChange={(e) => {
                            const currentDays = formData.days_of_week || [];
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                days_of_week: [...currentDays, day]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                days_of_week: currentDays.filter(d => d !== day)
                              }));
                            }
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Target Selection */}
              {formData.detection_method === 'classroom' ? (
              <div className="mb-3">
                <Form.Label>{t('form.targetedByClassrooms')} *</Form.Label>
                  <MultiSelectDropdown
                    options={classrooms.map(classroom => ({ value: classroom.id, label: classroom.name }))}
                    selectedValues={formData.classrooms || []}
                    onChange={(values) => setFormData(prev => ({ ...prev, classrooms: values as number[] }))}
                  placeholder={t('form.selectClassrooms')}
                />
              </div>
              ) : (
              <div className="mb-3">
                <Form.Label>{t('form.targetChildren')} *</Form.Label>
                  <MultiSelectDropdown
                    options={children.map(child => ({ value: child.id, label: `${child.name} (${child.classroom})` }))}
                    selectedValues={formData.child_ids || []}
                    onChange={(values) => setFormData(prev => ({ ...prev, child_ids: values as number[] }))}
                  placeholder={t('form.selectChildren')}
                />
              </div>
              )}

              <div className="mb-3">
                <Form.Label>{t('form.notificationRecipients')} *</Form.Label>
                <MultiSelectDropdown
                  options={users.map(user => ({ value: user.id, label: `${user.name} (${user.email})` }))}
                  selectedValues={formData.user_ids}
                  onChange={(values) => setFormData(prev => ({ ...prev, user_ids: values as number[] }))}
                  placeholder={t('form.selectUsers')}
                />
              </div>

              <div className="mb-3">
                <Form.Label>{t('form.notificationChannels')} *</Form.Label>
                <MultiSelectDropdown
                  options={['in_app', 'email', 'sms', 'webhook'].map(channel => ({ 
                    value: channel, 
                    label: channel.replace('_', ' ').toUpperCase() 
                  }))}
                  selectedValues={formData.notify_channels}
                  onChange={(values) => setFormData(prev => ({ ...prev, notify_channels: values as string[] }))}
                  placeholder={t('form.selectChannels')}
                />
              </div>

              <div className="mb-3">
                <Form.Label>{t('form.notificationCooldown')}</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={formData.repeat_interval_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, repeat_interval_minutes: Number(e.target.value) }))}
                  placeholder="180"
                />
                <Form.Text className="text-muted">
                  {t('form.cooldownHelp')}
                </Form.Text>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="secondary" 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingPolicy(null);
                  setEditError(null);
                  resetForm();
                }}
                disabled={loading}
              >
                {tc('buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {tc('status.updating')}
                  </>
                ) : (
                  <>
                    <EditIcon />
                    {t('updatePolicy')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* View Policy Modal */}
        <Modal show={showViewModal} onHide={closeViewModal} size="lg" centered scrollable>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('policyDetails')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-2 py-md-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {viewError && (
              <Alert variant="danger" dismissible onClose={() => setViewError(null)} className="mb-3">
                {viewError}
              </Alert>
            )}
            {viewModalLoading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" className="me-3" />
                <span>{t('loadingDetails')}</span>
              </div>
            ) : viewingPolicy ? (
              <div>
                <Row className="mb-3">
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.policyName')}</label>
                      <p className="mb-0">{viewingPolicy.name}</p>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('columns.status')}</label>
                      <div>
                        <Badge bg={viewingPolicy.is_enabled ? 'success' : 'secondary'}>
                          {viewingPolicy.is_enabled ? t('enabled') : t('disabled')}
                        </Badge>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.alertLevel')}</label>
                      <div>
                        <Badge bg={viewingPolicy.alert_level === 'red' ? 'danger' : 
                                 viewingPolicy.alert_level === 'yellow' ? 'warning' : 'secondary'}>
                          {viewingPolicy.alert_level.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.actionType')}</label>
                      <p className="mb-0">{actionTypeLabels[viewingPolicy.action_type] || viewingPolicy.action_type}</p>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.detectionMethod')}</label>
                      <p className="mb-0">{viewingPolicy.detection_method === 'classroom' ? t('byClassroom') : t('byChild')}</p>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.timeMode')}</label>
                      <p className="mb-0">{viewingPolicy.time_mode === 'anytime' ? t('form.anytime') : t('form.withinWindow')}</p>
                    </div>
                  </Col>
                </Row>

                {viewingPolicy.action_type === 'enter_classroom' && viewingPolicy.time_mode === 'within_window' && (
                  <>
                    <Row className="mb-3">
                      <Col xs={12} md={6}>
                        <div className="mb-3">
                          <label className="fw-semibold text-muted small">{t('form.startTime')}</label>
                          <p className="mb-0">{viewingPolicy.start_time}</p>
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div className="mb-3">
                          <label className="fw-semibold text-muted small">{t('form.endTime')}</label>
                          <p className="mb-0">{viewingPolicy.end_time}</p>
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col xs={12} md={6}>
                        <div className="mb-3">
                          <label className="fw-semibold text-muted small">{t('form.dateStart')}</label>
                          <p className="mb-0">{viewingPolicy.date_start || t('notSet')}</p>
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div className="mb-3">
                          <label className="fw-semibold text-muted small">{t('form.dateEnd')}</label>
                          <p className="mb-0">{viewingPolicy.date_end || t('notSet')}</p>
                        </div>
                      </Col>
                    </Row>

                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.daysOfWeek')}</label>
                      <p className="mb-0">
                        {Array.isArray(viewingPolicy.days_of_week) && viewingPolicy.days_of_week.length > 0
                          ? viewingPolicy.days_of_week.map(day => t(`form.days.${day.toLowerCase().slice(0, 3)}`, { defaultValue: day })).join(', ')
                          : viewingPolicy.days_of_week || t('none')}
                      </p>
                    </div>
                  </>
                )}

                {viewingPolicy.action_type === 'not_detected' && (
                  <Row className="mb-3">
                    <Col xs={12} md={6}>
                      <div className="mb-3">
                        <label className="fw-semibold text-muted small">{t('form.timeInterval')}</label>
                        <p className="mb-0">{viewingPolicy.timeslap || 15} {t('minutes')}</p>
                      </div>
                    </Col>
                  </Row>
                )}

                <div className="mb-3">
                  <label className="fw-semibold text-muted small">{t('columns.locations')}</label>
                  <div>
                    {viewingPolicy.locations && viewingPolicy.locations.length > 0 ? (
                      viewingPolicy.locations.map((location, index) => (
                        <Badge key={index} bg="info" className="me-2 mb-2">
                          {location.classroom_name}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-muted mb-0">{t('noLocationsSelected')}</p>
                    )}
                  </div>
                </div>

                {viewingPolicy.detection_method === 'classroom' && (
                  <div className="mb-3">
                    <label className="fw-semibold text-muted small">{t('targetClassrooms')}</label>
                    <div>
                      {viewingPolicy.classrooms && viewingPolicy.classrooms.length > 0 ? (
                        viewingPolicy.classrooms.map((classroom, index) => (
                          <Badge key={index} bg="primary" className="me-2 mb-2">
                            {classroom.classroom_name}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-muted mb-0">{t('noClassroomsSelected')}</p>
                      )}
                    </div>
                  </div>
                )}

                {viewingPolicy.detection_method === 'child' && (
                  <div className="mb-3">
                    <label className="fw-semibold text-muted small">{t('form.targetChildren')}</label>
                    <div>
                      {viewingPolicy.targets && viewingPolicy.targets.length > 0 ? (
                        viewingPolicy.targets.map((target, index) => (
                          <Badge key={index} bg="success" className="me-2 mb-2">
                            {target.name} ({target.child_id})
                          </Badge>
                        ))
                      ) : (
                        <p className="text-muted mb-0">{t('noChildrenSelected')}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="fw-semibold text-muted small">{t('form.notificationRecipients')}</label>
                  <p className="mb-0">
                    {viewingPolicy.recipients && viewingPolicy.recipients.length > 0 ? (
                      <span>{t('userCount', { count: viewingPolicy.recipients.length })}</span>
                    ) : (
                      <span className="text-muted">{t('noRecipientsSelected')}</span>
                    )}
                  </p>
                </div>

                <div className="mb-3">
                  <label className="fw-semibold text-muted small">{t('form.notificationChannels')}</label>
                  <div>
                    {viewingPolicy.notify_channels && viewingPolicy.notify_channels.length > 0 ? (
                      viewingPolicy.notify_channels.map((channel, index) => (
                        <Badge key={index} bg="secondary" className="me-2 mb-2">
                          {channel.replace('_', ' ').toUpperCase()}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-muted mb-0">{t('noChannelsSelected')}</p>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="fw-semibold text-muted small">{t('form.notificationCooldown')}</label>
                  <p className="mb-0">
                    {viewingPolicy.repeat_interval_minutes || 180} {t('minutes')} 
                    {viewingPolicy.repeat_interval_minutes === 180 && <span className="text-muted"> ({t('default')})</span>}
                  </p>
                  <Form.Text className="text-muted small">
                    {t('form.cooldownHelp')}
                  </Form.Text>
                </div>

                <Row className="mb-0">
                  <Col xs={12} md={6}>
                    <div className="mb-3 mb-md-0">
                      <label className="fw-semibold text-muted small">{tc('labels.createdAt')}</label>
                      <p className="mb-0">{formatDateTimeInNurseryTimezone(viewingPolicy.created_at, currentNursery?.timezone || 'UTC')}</p>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div>
                      <label className="fw-semibold text-muted small">{tc('labels.updatedAt')}</label>
                      <p className="mb-0">{formatDateTimeInNurseryTimezone(viewingPolicy.updated_at, currentNursery?.timezone || 'UTC')}</p>
                    </div>
                  </Col>
                </Row>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">{t('failedToLoad')}</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeViewModal}>
              {tc('buttons.close')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Policy Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => {
          setShowDeleteModal(false);
          setDeletingPolicy(null);
          setDeleteError(null);
        }} centered>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('deletePolicy')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-2 py-md-3">
            {deleteError && (
              <Alert variant="danger" dismissible onClose={() => setDeleteError(null)} className="mb-3">
                {deleteError}
              </Alert>
            )}
            <p>{t('deleteConfirm', { name: deletingPolicy?.name })}</p>
            <p className="text-muted mb-0">{tc('messages.cannotUndo')}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingPolicy(null);
                setDeleteError(null);
              }}
              disabled={loading}
            >
              {tc('buttons.cancel')}
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeletePolicy} 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {tc('status.deleting')}
                </>
              ) : (
                <>
                  <DeleteIcon />
                  {t('deletePolicy')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default Policies;
